import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../api";
import { formatAccountCode, formatMoney } from "../formatting";

export type AccountingEntry = {
  id: number; posting_date: string; account_code: string; account_name: string;
  voucher_number: string; document_no: string; description: string; debit: string; credit: string;
  source_kind: "eledger" | "general_ledger"; source_filename: string;
};

type Props = {
  invoice: { id: number; invoice_no: string; uuid?: string; direction?: string };
  documentTypeLabel?: string;
  showAccountingEntries?: boolean;
  previewUrl?: string;
  accountingEntries?: AccountingEntry[];
  onClose: () => void;
};

export function InvoicePreviewModal({ invoice, documentTypeLabel, showAccountingEntries = false, previewUrl, accountingEntries, onClose }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [entries, setEntries] = useState<AccountingEntry[] | null>(null);
  const [entriesError, setEntriesError] = useState("");
  useEffect(() => {
    setEntries(null);
    setEntriesError("");
    if (!showAccountingEntries) return;
    if (accountingEntries) { setEntries(accountingEntries); return; }
    const controller = new AbortController();
    void api<AccountingEntry[]>(`/api/invoices/${invoice.id}/accounting-entries`, { signal: controller.signal })
      .then(rows => { if (!controller.signal.aborted) setEntries(rows); })
      .catch(reason => { if (!controller.signal.aborted) setEntriesError(reason instanceof Error ? reason.message : "Muhasebe kayıtları yüklenemedi."); });
    return () => controller.abort();
  }, [invoice.id, showAccountingEntries, accountingEntries]);
  useLayoutEffect(() => {
    const element = dialog.current!;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const pagePosition = { left: window.scrollX, top: window.scrollY };
    const scrollParents: { element: HTMLElement; left: number; top: number }[] = [];
    for (let parent = previousFocus?.parentElement; parent; parent = parent.parentElement) {
      if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
        scrollParents.push({ element: parent, left: parent.scrollLeft, top: parent.scrollTop });
      }
    }
    document.body.style.overflow = "hidden";
    element.showModal();
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
      for (const parent of scrollParents) {
        if (parent.element.isConnected) parent.element.scrollTo({ left: parent.left, top: parent.top, behavior: "instant" });
      }
      window.scrollTo({ ...pagePosition, behavior: "instant" });
    };
  }, [invoice.id]);
  const label = invoice.invoice_no || invoice.uuid || `Fatura #${invoice.id}`;
  return <dialog ref={dialog} className="modal invoice-preview-modal" aria-label={`${label} fatura önizlemesi`}
    onCancel={event => { event.preventDefault(); onClose(); }}>
    <button type="button" className="modal-close" aria-label="Fatura önizlemesini kapat" onClick={onClose}>×</button>
    <div className="invoice-preview-head"><div><p className="eyebrow">FATURA ÖNİZLEME</p><h2>{label}</h2></div>
      {(documentTypeLabel || invoice.direction) && <span><strong>{documentTypeLabel}</strong>{invoice.direction && <small>{invoice.direction === "in" ? "Gelen / Alış" : "Giden / Satış"}</small>}</span>}
    </div>
    {showAccountingEntries && <section className="invoice-preview-accounting" aria-label="İlgili e-Defter ve muavin kayıtları">
      <h3>İlgili e-Defter / muavin kayıtları{entries && ` · ${entries.length} satır`}</h3>
      {entriesError ? <p role="alert">{entriesError}</p> : entries === null ? <p role="status">Muhasebe kayıtları yükleniyor…</p>
        : entries.length === 0 ? <p>Bu faturayla eşleşmiş e-Defter veya muavin kaydı bulunamadı.</p>
        : <div className="invoice-preview-accounting-table"><table>
          <thead><tr><th>Kayıt tarihi / fiş</th><th>Hesap kodu / adı</th><th>Açıklama / belge</th><th>Borç</th><th>Alacak</th><th>Kaynak</th></tr></thead>
          <tbody>{entries.map(entry => <tr key={entry.id}>
            <td>{entry.posting_date.split("-").reverse().join(".")}{entry.voucher_number && <small>Fiş: {entry.voucher_number}</small>}</td>
            <td>{formatAccountCode(entry.account_code)}{entry.account_name && <small>{entry.account_name}</small>}</td>
            <td>{entry.description || "—"}{entry.document_no && <small>{entry.document_no}</small>}</td>
            <td className="invoice-preview-amount">{formatMoney(entry.debit, "TRY", "tr-TR")}</td>
            <td className="invoice-preview-amount">{formatMoney(entry.credit, "TRY", "tr-TR")}</td>
            <td>{entry.source_kind === "eledger" ? "e-Defter" : "Muavin"}<small>{entry.source_filename}</small></td>
          </tr>)}</tbody>
        </table></div>}
    </section>}
    <iframe className="invoice-preview-frame" title={`${label} içeriği`} src={previewUrl || `/api/invoices/${invoice.id}/preview`} sandbox="" />
    <div className="invoice-preview-actions">
      <a className="secondary-button" href={previewUrl || `/api/invoices/${invoice.id}/preview`} target="_blank" rel="noreferrer">Yeni sekmede aç</a>
      {!previewUrl && <a className="primary-button" href={`/api/invoices/${invoice.id}/artifact`}>Ham belgeyi indir</a>}
    </div>
  </dialog>;
}
